import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { MapPin, Phone } from 'lucide-react';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const STATUS_CHIP = {
  booked: 'text-orange-400 bg-orange-400/10',
  paid_in_full: 'text-emerald-400 bg-emerald-400/10',
  invoice_sent: 'text-amber-400 bg-amber-400/10',
  completed: 'text-zinc-500 bg-white/5',
};

export const EventsCalendar = ({ onOpenLead }) => {
  const [events, setEvents] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/pipeline');
      const cards = data.columns.flatMap(c => c.cards).filter(p => p.event_date && ['booked', 'paid_in_full', 'invoice_sent', 'completed'].includes(p.status));
      cards.sort((a, b) => (a.event_date > b.event_date ? 1 : -1));
      setEvents(cards);
    } catch (err) {
      console.error('Failed to load events', err);
      setEvents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (events === null) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading events…</p>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.event_date >= today);
  const past = events.filter(e => e.event_date < today).reverse();

  const monthOf = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const grouped = upcoming.reduce((acc, e) => {
    const m = monthOf(e.event_date);
    (acc[m] = acc[m] || []).push(e);
    return acc;
  }, {});

  const EventRow = ({ e }) => (
    <div className="flex items-center gap-4 bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 cursor-pointer active:scale-[0.98] transition-transform"
         onClick={() => onOpenLead && onOpenLead(e.proposal_id)} data-testid={`event-${e.proposal_id}`}>
      <div className="text-center w-11 flex-shrink-0">
        <p className="text-white text-[22px] font-black tracking-tight leading-none tabular-nums" style={FONT_H}>{e.event_date.slice(8, 10)}</p>
        <p className="text-zinc-600 text-[10px] uppercase tracking-wide mt-1" style={FONT_B}>{new Date(e.event_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-[15px] font-bold tracking-tight truncate" style={FONT_H}>{e.client_name}</p>
        <p className="text-zinc-500 text-xs truncate" style={FONT_B}>
          {e.event_type || 'Event'}{e.event_time ? ` · ${e.event_time}` : ''}
        </p>
        {e.event_location && (
          <p className="text-zinc-600 text-xs truncate flex items-center gap-1 mt-0.5" style={FONT_B}><MapPin className="w-3 h-3 flex-shrink-0" />{e.event_location}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-white text-sm font-bold tabular-nums" style={FONT_H}>{fmt(e.total)}</p>
        <span className={`text-[9px] uppercase tracking-wide px-2 py-0.5 rounded-md font-bold ${STATUS_CHIP[e.status] || 'text-zinc-500 bg-white/5'}`} style={FONT_B}>
          {e.status === 'invoice_sent' ? 'Unpaid' : e.status === 'booked' ? 'Deposit Paid' : e.status === 'paid_in_full' ? 'Paid in Full' : e.status}
        </span>
      </div>
      {e.client_phone && (
        <a href={`tel:${e.client_phone}`} onClick={(ev) => ev.stopPropagation()} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 flex-shrink-0 transition-transform" aria-label="Call">
          <Phone className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  return (
    <div data-testid="events-calendar" className="space-y-9">
      {Object.keys(grouped).length === 0 && (
        <p className="text-zinc-600 text-sm text-center py-16" style={FONT_B}>No upcoming events</p>
      )}
      {Object.entries(grouped).map(([month, list]) => (
        <div key={month}>
          <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold mb-4" style={FONT_B}>{month}</p>
          <div className="space-y-3">{list.map(e => <EventRow key={e.proposal_id} e={e} />)}</div>
        </div>
      ))}
      {past.length > 0 && (
        <div>
          <p className="text-zinc-700 text-[11px] uppercase tracking-[0.2em] font-semibold mb-4" style={FONT_B}>Past Events</p>
          <div className="space-y-3 opacity-50">{past.slice(0, 10).map(e => <EventRow key={e.proposal_id} e={e} />)}</div>
        </div>
      )}
    </div>
  );
};
